import { useSearchParams } from "react-router-dom";
import { Banner, Spinner } from "@NP-Webber/crm-kit/dist/next/index.js";
import { ScreenHeader } from "../../next/page/ScreenHeader";
import { useEntityLabels } from "../../i18n/EntityLabelsProvider";
import { CategoriesTab } from "./CategoriesTab";
import { FundsTab } from "./FundsTab";
import { BudgetsTab } from "./BudgetsTab";
import { CampaignsMappingsTab } from "./CampaignsMappingsTab";
import { NedarimMappingsTab } from "./NedarimMappingsTab";
import { useCustomerScope } from "./useCustomerScope";
import "./financeStructure.css";

const SUB_TABS = [
  { key: "categories", label: "קטגוריות" },
  { key: "funds", label: "קופות" },
  { key: "budgets", label: "תקציבים" },
  { key: "campaigns", label: "קמפיינים" },
  { key: "nedarim", label: "מיפוי נדרים" },
] as const;

type SubTab = (typeof SUB_TABS)[number]["key"];

export function StructurePage() {
  const labels = useEntityLabels();
  const { writeCustomerId, loading, error } = useCustomerScope();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const activeTab: SubTab = SUB_TABS.some((t) => t.key === tabParam) ? (tabParam as SubTab) : "categories";
  const setActiveTab = (key: SubTab) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    next.delete("focus");
    setParams(next, { replace: true });
  };

  return (
    <div className="page-section fs-page">
      <ScreenHeader sub="קטגוריות, קופות פנימיות, תקציבים ומיפויי קמפיינים ונדרים" />
      {loading ? (
        <div className="ck-empty">
          <Spinner size={24} />
        </div>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {!loading && !writeCustomerId ? (
        <Banner tone="info">
          אין {labels.customers} פעילים — צרו {labels.customer} תחילה.
        </Banner>
      ) : null}

      {!loading && writeCustomerId ? (
        <>
          <nav className="fs-tabs tb-tabs" role="tablist">
            {SUB_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`tb-tabs__tab${activeTab === tab.key ? " is-active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {activeTab === "categories" ? (
            <CategoriesTab writeCustomerId={writeCustomerId} />
          ) : null}
          {activeTab === "funds" ? (
            <FundsTab writeCustomerId={writeCustomerId} />
          ) : null}
          {activeTab === "budgets" ? (
            <BudgetsTab writeCustomerId={writeCustomerId} />
          ) : null}
          {activeTab === "campaigns" ? (
            <CampaignsMappingsTab writeCustomerId={writeCustomerId} />
          ) : null}
          {activeTab === "nedarim" ? (
            <NedarimMappingsTab writeCustomerId={writeCustomerId} />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
